import React, { useMemo } from 'react';
import { getChampionArchetype, generateChampionTags, ARCHETYPE_LABELS } from '../lib/championArchetypes';

/**
 * APEX v8.7 — ChampionCoachTags
 *
 * Tags de coaching façon Porofessor, habillés design system néon.
 *   - strength : cyan   (forces, win conditions)
 *   - caution  : ambre  (nuances, conditions)
 *   - warning  : rose   (alertes générées depuis les stats réelles du joueur)
 *
 * Props:
 *   champion   : nom du champion
 *   games      : toutes les games (pour calculer les stats du champion)
 *   role       : rôle (default ADC)
 */

const TAG_STYLES = {
  strength: 'border-cyan-400/40 bg-cyan-400/10 text-cyan-300',
  caution:  'border-amber-400/40 bg-amber-400/10 text-amber-300',
  warning:  'border-rose-400/50 bg-rose-400/10 text-rose-300',
};

function computeChampStats(champion, games) {
  const champGames = (Array.isArray(games) ? games : []).filter(g => g.champion === champion);
  if (champGames.length === 0) return null;

  const wins = champGames.filter(g => g.result === 'W').length;
  const deaths = champGames.reduce((s, g) => s + (g.deaths || 0), 0);
  const csVals = champGames.filter(g => g.csPerMin).map(g => g.csPerMin);
  const avgCs = csVals.length ? csVals.reduce((a, b) => a + b, 0) / csVals.length : null;

  // Lane WR @15 si dispo dans les games
  const laneVals = champGames.filter(g => typeof g.laneWinrate === 'number' || typeof g.lane_wr_15 === 'number');
  const laneWr = laneVals.length
    ? laneVals.reduce((s, g) => s + (g.laneWinrate ?? g.lane_wr_15), 0) / laneVals.length
    : null;

  return {
    games: champGames.length,
    wins,
    avgDeaths: deaths / champGames.length,
    avgCs,
    laneWr,
  };
}

export default function ChampionCoachTags({ champion, games, role = 'ADC' }) {
  const arch = getChampionArchetype(champion, role);

  const tags = useMemo(() => {
    if (!champion) return [];
    const stats = computeChampStats(champion, games);
    return generateChampionTags(champion, stats, role);
  }, [champion, games, role]);

  if (!arch || tags.length === 0) return null;

  const archLabel = ARCHETYPE_LABELS[arch.archetype] || arch.archetype;
  const subLabels = (arch.subArchetypes || [])
    .map(s => ARCHETYPE_LABELS[s] || s)
    .join(' · ');

  return (
    <div className="border border-zinc-800 bg-zinc-950 p-4">
      {/* Header champion + archétype */}
      <div className="flex items-baseline gap-3 mb-3">
        <span className="text-sm font-black text-white tracking-wide uppercase">{champion}</span>
        <span className="text-[10px] tracking-[0.25em] uppercase font-bold text-cyan-400">
          {archLabel}
        </span>
        {subLabels && (
          <span className="text-[9px] tracking-[0.2em] uppercase text-zinc-600">
            {subLabels}
          </span>
        )}
      </div>

      {/* Win condition */}
      {arch.winCondition && (
        <div className="text-[11px] text-zinc-400 leading-relaxed mb-3 border-l-2 border-zinc-800 pl-3">
          {arch.winCondition}
        </div>
      )}

      {/* Tags */}
      <div className="flex flex-wrap gap-1.5">
        {tags.map((tag) => (
          <span
            key={`${tag.type}-${tag.label}`}
            className={`text-[10px] font-bold tracking-wide px-2 py-1 border ${TAG_STYLES[tag.type] || TAG_STYLES.caution}`}
          >
            {tag.type === 'strength' && '▲ '}
            {tag.type === 'warning' && '⚠ '}
            {tag.label}
          </span>
        ))}
      </div>

      {/* Power spike */}
      {arch.powerSpike && (
        <div className="mt-3 text-[10px] text-zinc-500 leading-relaxed">
          <span className="text-zinc-600 font-bold uppercase tracking-wider">Power spike — </span>
          {arch.powerSpike}
        </div>
      )}
    </div>
  );
}
